import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import type { DeepPushOnlyArray } from '@seen/core/monitorResource'
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export interface UseFhirPatchMutationProxy<T extends ResourceType> {
  resource: ExtractResource<T>
  mutateFn: (resource: DeepPushOnlyArray<ExtractResource<T>>) => void
}

export interface UseFhirPatchMutationProxyOptions<T extends ResourceType> {
  mutation?: Omit<
    UseMutationOptions<
      ExtractResource<T>,
      unknown,
      UseFhirPatchMutationProxy<T>
    >,
    'mutationFn'
  >
}

/**
 * @param type - The resource type.
 * @param options - The options.
 * @returns The query result.
 * @deprecated Use usePatchMutation instead
 */
export function useFhirPatchMutation<T extends ResourceType>(
  type: T | undefined,
  options?: UseFhirPatchMutationProxyOptions<T> | null
): UseMutationResult<
  ExtractResource<T>,
  unknown,
  UseFhirPatchMutationProxy<T>
> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()
  return useMutation({
    ...options?.mutation,
    onSuccess: (data, variables, context) => {
      invariant(data.id, 'ID is required')
      cacheKeys.invalidateMutation(queryClient, type, data.id)
      queryClient.setQueryData(cacheKeys.read(type, data.id), data)
      options?.mutation?.onSuccess?.(data, variables, context)
    },
    mutationFn: async (args) => {
      if (isReadOnly) {
        console.warn(
          '[read-only] mutation attempted: useFhirPatchMutation',
          type
        )
      }
      return medplum.patchResourceProxy(args.resource, args.mutateFn)
    },
  })
}
